import { ErrorWithStatusCode } from '../network/fetchWithRetry.ts';
import makeHappoAPIRequest from '../network/makeHappoAPIRequest.ts';
import createHash from '../utils/createHash.ts';
import type {
  BrowserType,
  ConfigWithDefaults,
  Page,
  TargetWithDefaults,
} from './index.ts';

const VIEWPORT_PATTERN = /^([0-9]+)x([0-9]+)$/;

/**
 * Maximum number of chunk items sent in a single bulk request.
 * Keeps individual payloads bounded while still protecting against
 * arbitrarily large explicit `chunks` values exceeding server limits.
 */
const MAX_BULK_ITEMS_PER_REQUEST = 50;

/**
 * Compute the number of chunks to use based on an estimated snapshot count.
 *
 * Aims for roughly 100 items per chunk, capped at 20. Returns 1 for
 * non-positive or non-finite inputs.
 */
function computeDefaultChunks(estimatedSnapCount: number): number {
  if (!Number.isFinite(estimatedSnapCount) || estimatedSnapCount <= 0) {
    return 1;
  }

  return Math.min(20, Math.ceil(estimatedSnapCount / 100));
}

/**
 * PageSlice is an array of pages with the extra extendsSha property.
 */
interface PageSlice extends Array<Page> {
  extendsSha?: string;
}

interface Chunk {
  index: number;
  total: number;
}

interface ChunkItem {
  type: string;
  targetName: string | undefined;
  payloadString: string;
  payloadHash: string;
  extendsSha?: string;
}

export interface CSSBlock {
  id: string;
  conditional: boolean;
  css: string;
}

export interface ExecuteParams {
  globalCSS?: string | Array<CSSBlock>;

  /** Path to the assets package */
  assetsPackage?: string;

  /** Path to the static package */
  staticPackage?: string;

  snapPayloads?: Array<unknown>;
  pages?: Array<Page>;
  targetName?: string;

  /**
   * Total number of snapshots in the package. When provided for staticPackage
   * requests without explicit chunks, used to automatically determine the
   * optimal number of parallel chunks.
   */
  estimatedSnapsCount?: number;

  /**
   * When true, the worker fails the snap if `waitForContent` or
   * `waitForSelector` times out instead of silently warning.
   */
  failOnWaitForTimeout?: boolean;
}

function getPageSlices(pages: Array<Page>, chunks: number): Array<PageSlice> {
  const result: Array<PageSlice> = [];

  // First, split the raw pages into chunks
  const pagesPerChunk = Math.ceil(pages.length / chunks);
  for (let i = 0; i < chunks; i += 1) {
    const pageSlice = pages.slice(
      i * pagesPerChunk,
      i * pagesPerChunk + pagesPerChunk,
    );

    if (pageSlice.length > 0) {
      result.push(pageSlice);
    }
  }
  return result;
}

function buildChunkItem({
  slice,
  chunk,
  pageSlice,
  browserName,
  viewport,
  maxHeight,
  otherOptions,
  globalCSS,
  staticPackage,
  assetsPackage,
  targetName,
  failOnWaitForTimeout,
}: {
  slice?: Array<unknown> | undefined;
  chunk?: Chunk | undefined;
  pageSlice?: PageSlice | undefined;
  browserName: BrowserType;
  viewport: string;
  maxHeight: number | undefined;
  otherOptions: Record<string, unknown>;
  globalCSS: string | Array<CSSBlock> | undefined;
  staticPackage: string | undefined;
  assetsPackage: string | undefined;
  targetName: string | undefined;
  failOnWaitForTimeout: boolean | undefined;
}): ChunkItem {
  const payloadString = JSON.stringify({
    viewport,
    maxHeight,
    ...otherOptions,
    globalCSS,
    snapPayloads: slice,
    chunk,
    staticPackage,
    assetsPackage,
    pages: pageSlice,
    extendsSha: pageSlice ? pageSlice.extendsSha : undefined,
    failOnWaitForTimeout,
  });

  const payloadHash = createHash(payloadString + (pageSlice ? Math.random() : ''));

  const type =
    pageSlice && pageSlice.extendsSha ? 'extends-report' : `browser-${browserName}`;

  const item: ChunkItem = { type, targetName, payloadString, payloadHash };
  if (pageSlice?.extendsSha) {
    item.extendsSha = pageSlice.extendsSha;
  }
  return item;
}

async function sendIndividualSnapRequest(
  item: ChunkItem,
  config: ConfigWithDefaults,
): Promise<number> {
  const formData: Record<string, string | number | File | undefined> = {
    type: item.type,
    targetName: item.targetName,
    payloadHash: item.payloadHash,
    payload: new File([item.payloadString], 'payload.json', {
      type: 'application/json',
    }),
  };

  if (item.extendsSha) {
    formData.extendsSha = item.extendsSha;
  }

  // We `await` here inside the loop to avoid POSTing all payloads to the
  // server at the same time (thus reducing load a little).
  const requestResult = await makeHappoAPIRequest(
    {
      path: `/api/snap-requests?payloadHash=${item.payloadHash}`,
      method: 'POST',
      formData,
    },
    config,
    { retryCount: 5 },
  );

  if (!requestResult) {
    throw new Error('No requestResult');
  }

  if (!('requestId' in requestResult)) {
    throw new Error('No requestId in requestResult');
  }

  if (typeof requestResult.requestId !== 'number') {
    throw new TypeError('requestId is not a number');
  }

  return requestResult.requestId;
}

export default class RemoteBrowserTarget {
  public readonly chunks: number | undefined;
  public readonly browserName: BrowserType;
  public readonly viewport: string;
  public readonly maxHeight: number | undefined;
  public readonly otherOptions: Record<string, unknown>;

  constructor(
    browserName: BrowserType,
    {
      viewport = '1024x768',
      chunks,
      maxHeight,
      ...otherOptions
    }: TargetWithDefaults,
  ) {
    if (!browserName) {
      throw new Error(
        `Invalid browser type: "${browserName}". Make sure the "type" field in your target configuration is set to a valid browser type.`,
      );
    }

    const viewportMatch = viewport.match(VIEWPORT_PATTERN);
    if (!viewportMatch) {
      throw new Error(
        `Invalid viewport "${viewport}". Here's an example of a valid one: "1024x768".`,
      );
    }

    this.chunks = chunks;
    this.browserName = browserName;
    this.viewport = viewport;
    this.maxHeight = maxHeight ?? undefined;
    this.otherOptions = otherOptions;
  }

  async execute(
    {
      globalCSS,
      assetsPackage,
      staticPackage,
      snapPayloads,
      pages,
      targetName,
      estimatedSnapsCount,
      failOnWaitForTimeout,
    }: ExecuteParams,
    config: ConfigWithDefaults,
  ): Promise<Array<number>> {
    const buildItemParams = {
      browserName: this.browserName,
      viewport: this.viewport,
      maxHeight: this.maxHeight,
      otherOptions: this.otherOptions,
      globalCSS,
      staticPackage,
      assetsPackage,
      targetName,
      failOnWaitForTimeout,
    };

    // Build all chunk items up front
    const items: Array<ChunkItem> = [];

    if (staticPackage) {
      const effectiveChunks =
        this.chunks ?? Math.max(1, computeDefaultChunks(estimatedSnapsCount ?? 0));
      for (let i = 0; i < effectiveChunks; i += 1) {
        items.push(
          buildChunkItem({
            ...buildItemParams,
            chunk:
              effectiveChunks > 1 ? { index: i, total: effectiveChunks } : undefined,
          }),
        );
      }
    } else if (pages) {
      for (const pageSlice of getPageSlices(pages, this.chunks ?? 1)) {
        items.push(buildChunkItem({ ...buildItemParams, pageSlice }));
      }
    } else {
      const effectiveChunks = this.chunks ?? 1;
      const snapsPerChunk = Math.ceil((snapPayloads?.length ?? 0) / effectiveChunks);
      for (let i = 0; i < effectiveChunks; i += 1) {
        const slice = snapPayloads?.slice(
          i * snapsPerChunk,
          i * snapsPerChunk + snapsPerChunk,
        );
        items.push(buildChunkItem({ ...buildItemParams, slice }));
      }
    }

    if (items.length === 0) {
      return [];
    }

    // Try the bulk endpoint first. If it is unavailable, fall back to individual
    // requests. If it responds with an unexpected payload shape, fail fast to
    // avoid creating duplicate snap-requests.
    //
    // Large item arrays are split into batches of MAX_BULK_ITEMS_PER_REQUEST
    // and sent as sequential bulk requests to keep individual payloads bounded.
    try {
      const requestIds: Array<number | undefined> = Array.from({
        length: items.length,
      });

      for (
        let batchStart = 0;
        batchStart < items.length;
        batchStart += MAX_BULK_ITEMS_PER_REQUEST
      ) {
        const batch = items.slice(
          batchStart,
          batchStart + MAX_BULK_ITEMS_PER_REQUEST,
        );

        const result = await makeHappoAPIRequest(
          {
            path: '/api/snap-requests/bulk',
            method: 'POST',
            body: { items: batch },
          },
          config,
          { retryCount: 5 },
        );

        if (
          result &&
          'results' in result &&
          Array.isArray(result.results) &&
          result.results.length === batch.length
        ) {
          const bulkResults = result.results as Array<{
            requestId?: number;
            error?: string;
          }>;

          for (const [i, r] of bulkResults.entries()) {
            requestIds[batchStart + i] =
              typeof r.requestId === 'number' ? r.requestId : undefined;
          }
        } else {
          // The bulk endpoint responded with a 200 but an unexpected payload
          // shape. Fail fast instead of falling back to avoid potentially
          // creating duplicate snap-requests.
          throw new Error(
            'Bulk snap-requests endpoint returned an unexpected payload shape; aborting to avoid duplicate snap-requests.',
          );
        }
      }

      // Retry any failed items individually (sequentially to reduce load)
      for (const [i, item] of items.entries()) {
        if (requestIds[i] === undefined) {
          requestIds[i] = await sendIndividualSnapRequest(item, config);
        }
      }

      return requestIds.map((id, index) => {
        if (id === undefined) {
          throw new Error(
            `Failed to obtain snap request ID for item at index ${index}`,
          );
        }

        return id;
      });
    } catch (error) {
      // Fall back to individual requests only when the server explicitly
      // reports that the bulk endpoint is missing or not implemented.
      if (
        !(
          error instanceof ErrorWithStatusCode &&
          (error.statusCode === 404 || error.statusCode === 501)
        )
      ) {
        throw error;
      }
    }

    // Fallback: sequential individual requests (for older happo deployments)
    const requestIds: Array<number> = [];

    for (const item of items) {
      requestIds.push(await sendIndividualSnapRequest(item, config));
    }

    return requestIds;
  }
}
